import React, { useEffect, useRef, useState } from 'react';

/**
 * Cuenta desde 0 hasta el número contenido en `value` cuando entra en pantalla.
 * Acepta '15+', '40+', '1,240', 10… (conserva prefijo/sufijo no numérico).
 * En el HTML prerenderizado se imprime el valor final (sin JS no hay animación).
 */
const parse = (value) => {
    const str = String(value ?? '');
    const m = str.match(/^([^\d]*)([\d.,]+)([^\d]*)$/);
    if (!m) return null;
    const num = parseFloat(m[2].replace(/,/g, ''));
    if (Number.isNaN(num)) return null;
    return { prefix: m[1], num, suffix: m[3], hasComma: m[2].includes(','), decimals: (m[2].split('.')[1] || '').length };
};

const CountUp = ({ value, duration = 1400, className = '' }) => {
    const parsed = parse(value);
    const ref = useRef(null);
    const [display, setDisplay] = useState(String(value ?? ''));
    const done = useRef(false);

    useEffect(() => {
        if (!parsed || !ref.current || done.current) return undefined;
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || window.__PRERENDERING__) return undefined;

        const format = (n) => {
            const fixed = n.toFixed(parsed.decimals);
            return `${parsed.prefix}${parsed.hasComma ? Number(fixed).toLocaleString('en-US') : fixed}${parsed.suffix}`;
        };

        const el = ref.current;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || done.current) return;
                done.current = true;
                obs.disconnect();
                const start = performance.now();
                const tick = (now) => {
                    const t = Math.min(1, (now - start) / duration);
                    const eased = 1 - Math.pow(1 - t, 3);
                    setDisplay(format(parsed.num * eased));
                    if (t < 1) requestAnimationFrame(tick);
                    else setDisplay(String(value));
                };
                setDisplay(format(0));
                requestAnimationFrame(tick);
            },
            { threshold: 0.5 }
        );
        obs.observe(el);
        return () => obs.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, duration]);

    return (
        <span ref={ref} className={`countup ${className}`.trim()}>
            {display}
        </span>
    );
};

export default CountUp;
